// ============================================================================
// REACT QUERY HOOKS FOR AI INTERVIEW PLANS
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import type {
  AIInterviewPlanTemplate,
  CreateAIInterviewPlanRequest,
  UpdateAIInterviewPlanRequest,
} from '@ispot/shared'

import { aiInterviewPlanApi } from '@/lib/api/client'

// ============================================================================
// QUERY KEY FACTORY
// ============================================================================

const planKeys = {
  all: (accountId: string, projectId: string) =>
    ['aiInterviewPlans', accountId, projectId] as const,
  detail: (accountId: string, projectId: string, planId: string) =>
    ['aiInterviewPlan', accountId, projectId, planId] as const,
  sessions: (accountId: string, projectId: string, planId: string) =>
    ['aiInterviewPlanSessions', accountId, projectId, planId] as const,
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/** List all interview plans for a project */
export function usePlans(accountId: string, projectId: string) {
  return useQuery({
    queryKey: planKeys.all(accountId, projectId),
    queryFn: () => aiInterviewPlanApi.listPlans(accountId, projectId),
    staleTime: 30_000,
    enabled: !!accountId && !!projectId,
  })
}

/** Get a single interview plan by ID */
export function usePlan(accountId: string, projectId: string, planId: string) {
  return useQuery({
    queryKey: planKeys.detail(accountId, projectId, planId),
    queryFn: () => aiInterviewPlanApi.getPlan(accountId, projectId, planId),
    staleTime: 30_000,
    enabled: !!accountId && !!projectId && !!planId,
  })
}

/** List sessions for a specific plan */
export function usePlanSessions(
  accountId: string,
  projectId: string,
  planId: string,
) {
  return useQuery({
    queryKey: planKeys.sessions(accountId, projectId, planId),
    queryFn: () =>
      aiInterviewPlanApi.listSessions(accountId, projectId, planId),
    staleTime: 30_000,
    enabled: !!accountId && !!projectId && !!planId,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/** Create a new interview plan */
export function useCreatePlan(accountId: string, projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAIInterviewPlanRequest) =>
      aiInterviewPlanApi.createPlan(accountId, projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: planKeys.all(accountId, projectId),
      })
    },
  })
}

/** Update an existing interview plan */
export function useUpdatePlan(accountId: string, projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      planId,
      data,
    }: {
      planId: string
      data: UpdateAIInterviewPlanRequest
    }) => aiInterviewPlanApi.updatePlan(accountId, projectId, planId, data),
    onSuccess: (_result, { planId }) => {
      queryClient.invalidateQueries({
        queryKey: planKeys.all(accountId, projectId),
      })
      queryClient.invalidateQueries({
        queryKey: planKeys.detail(accountId, projectId, planId),
      })
    },
  })
}

/** Delete an interview plan */
export function useDeletePlan(accountId: string, projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planId: string) =>
      aiInterviewPlanApi.deletePlan(accountId, projectId, planId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: planKeys.all(accountId, projectId),
      })
    },
  })
}

/** Activate a plan (makes it available for interviews) */
export function useActivatePlan(accountId: string, projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planId: string) =>
      aiInterviewPlanApi.activatePlan(accountId, projectId, planId),
    onSuccess: (_result, planId) => {
      queryClient.invalidateQueries({
        queryKey: planKeys.all(accountId, projectId),
      })
      queryClient.invalidateQueries({
        queryKey: planKeys.detail(accountId, projectId, planId),
      })
    },
  })
}

/** Deactivate a plan (stops accepting new interviews) */
export function useDeactivatePlan(accountId: string, projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planId: string) =>
      aiInterviewPlanApi.deactivatePlan(accountId, projectId, planId),
    onSuccess: (_result, planId) => {
      queryClient.invalidateQueries({
        queryKey: planKeys.all(accountId, projectId),
      })
      queryClient.invalidateQueries({
        queryKey: planKeys.detail(accountId, projectId, planId),
      })
    },
  })
}

/** Regenerate the share link for a plan */
export function useRegenerateLink(accountId: string, projectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planId: string) =>
      aiInterviewPlanApi.regenerateLink(accountId, projectId, planId),
    onSuccess: (_result, planId) => {
      queryClient.invalidateQueries({
        queryKey: planKeys.detail(accountId, projectId, planId),
      })
    },
  })
}
